import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  LogOut, Search, Users, Home as HomeIcon, CheckCircle2, Clock, X, Phone, Mail,
  LayoutGrid, ListChecks, StickyNote, Image as ImageIcon, MapPin as MapPinIcon,
} from "lucide-react";
import { api, useAuth } from "./AuthContext";
import { useLang } from "../i18n/LanguageContext";
import AdminApartments from "./AdminApartments";
import AdminGallery from "./AdminGallery";
import AdminSettings from "./AdminSettings";

const LEAD_STATUSES = ["New", "Contacted", "Viewing Scheduled", "Negotiating", "Sold", "Lost"];
const INV_STATUSES = ["Available", "Reserved", "Sold"];

const statusStyle = {
  New: "bg-blue-100 text-blue-800 border-blue-200",
  Contacted: "bg-amber-100 text-amber-800 border-amber-200",
  "Viewing Scheduled": "bg-indigo-100 text-indigo-800 border-indigo-200",
  Negotiating: "bg-[#D4AF37]/20 text-[#8a6d1a] border-[#D4AF37]/40",
  Sold: "bg-emerald-100 text-emerald-800 border-emerald-200",
  Lost: "bg-red-100 text-red-700 border-red-200",
};
const invStyle = {
  Available: "bg-emerald-100 text-emerald-800 border-emerald-300",
  Reserved: "bg-[#D4AF37]/20 text-[#8a6d1a] border-[#D4AF37]/50",
  Sold: "bg-red-100 text-red-700 border-red-300",
};

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleString("lt-LT", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
};

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-white border border-[#E4E4E7] rounded-lg p-5 flex items-center gap-4" data-testid={`stat-${label}`}>
      <div className={`w-11 h-11 rounded-lg flex items-center justify-center ${color}`}>
        <Icon size={20} />
      </div>
      <div>
        <p className="text-2xl font-semibold text-[#1A1A1A]">{value}</p>
        <p className="text-xs text-[#52525B] uppercase tracking-wide">{label}</p>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { admin, logout } = useAuth();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [tab, setTab] = useState("leads");
  const [leads, setLeads] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState("");
  const [fBuilding, setFBuilding] = useState("");
  const [fStatus, setFStatus] = useState("");
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");

  const st = (key) => t(`statuses.${key}`);

  const loadLeads = useCallback(async () => {
    const params = {};
    if (search) params.search = search;
    if (fBuilding) params.building = fBuilding;
    if (fStatus) params.status = fStatus;
    const { data } = await api.get("/admin/leads", { params });
    setLeads(data);
  }, [search, fBuilding, fStatus]);

  const loadInventory = useCallback(async () => {
    const { data } = await api.get("/admin/inventory");
    setInventory(data);
  }, []);

  const loadStats = useCallback(async () => {
    const { data } = await api.get("/admin/stats");
    setStats(data);
  }, []);

  useEffect(() => { loadStats(); loadInventory(); }, [loadStats, loadInventory]);
  useEffect(() => { loadLeads(); }, [loadLeads]);

  const handleLogout = async () => { await logout(); navigate("/admin"); };

  const updateStatus = async (id, status) => {
    await api.patch(`/admin/leads/${id}/status`, { status });
    await loadLeads(); await loadStats();
    if (selected?.id === id) setSelected({ ...selected, status });
  };

  const addNote = async () => {
    if (!note.trim() || !selected) return;
    const { data } = await api.post(`/admin/leads/${selected.id}/notes`, { text: note });
    setSelected({ ...selected, notes: [...(selected.notes || []), data] });
    setNote("");
    loadLeads();
  };

  const updateInventory = async (code, status) => {
    await api.patch(`/admin/inventory/${encodeURIComponent(code)}`, { status });
    await loadInventory(); await loadStats();
  };

  const byBuilding = inventory.reduce((acc, i) => {
    (acc[i.building] = acc[i.building] || []).push(i);
    return acc;
  }, {});

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#F5F3EE" }} data-testid="crm-dashboard">
      {/* Topbar */}
      <header className="bg-white border-b border-[#E4E4E7] sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="font-serif text-xl text-[#1A1A1A]">Sakurų namai</span>
            <span className="text-[10px] uppercase tracking-widest text-[#8a6d1a] bg-[#D4AF37]/15 px-2 py-1 rounded">CRM</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-[#52525B] hidden sm:inline">{admin?.username}</span>
            <button onClick={handleLogout} data-testid="admin-logout" className="inline-flex items-center gap-2 text-sm text-[#1A1A1A] border border-[#E4E4E7] rounded px-3 py-1.5 hover:bg-[#F5F3EE]">
              <LogOut size={15} /> {t("admin.logout")}
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 py-8">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatCard icon={Users} label={t("admin.totalLeads")} value={stats.total_leads} color="bg-blue-100 text-blue-700" />
            <StatCard icon={Clock} label={t("admin.newLeads")} value={stats.new_leads} color="bg-amber-100 text-amber-700" />
            <StatCard icon={HomeIcon} label={t("admin.availableUnits")} value={stats.inventory.available} color="bg-emerald-100 text-emerald-700" />
            <StatCard icon={CheckCircle2} label={t("admin.soldUnits")} value={stats.inventory.sold} color="bg-red-100 text-red-700" />
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button onClick={() => setTab("leads")} data-testid="tab-leads" className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === "leads" ? "bg-[#1A1A1A] text-white" : "bg-white text-[#52525B] border border-[#E4E4E7]"}`}>
            <ListChecks size={16} /> {t("admin.leads")}
          </button>
          <button onClick={() => setTab("inventory")} data-testid="tab-inventory" className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === "inventory" ? "bg-[#1A1A1A] text-white" : "bg-white text-[#52525B] border border-[#E4E4E7]"}`}>
            <LayoutGrid size={16} /> {t("admin.inventory")}
          </button>
          <button onClick={() => setTab("apartments")} data-testid="tab-apartments" className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === "apartments" ? "bg-[#1A1A1A] text-white" : "bg-white text-[#52525B] border border-[#E4E4E7]"}`}>
            <HomeIcon size={16} /> Butai
          </button>
          <button onClick={() => setTab("gallery")} data-testid="tab-gallery" className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === "gallery" ? "bg-[#1A1A1A] text-white" : "bg-white text-[#52525B] border border-[#E4E4E7]"}`}>
            <ImageIcon size={16} /> Galerija
          </button>
          <button onClick={() => setTab("surroundings")} data-testid="tab-surroundings" className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === "surroundings" ? "bg-[#1A1A1A] text-white" : "bg-white text-[#52525B] border border-[#E4E4E7]"}`}>
            <MapPinIcon size={16} /> Aplinka
          </button>
        </div>

        {tab === "apartments" && <AdminApartments />}
        {tab === "gallery" && <AdminGallery />}
        {tab === "surroundings" && <AdminSettings />}

        {tab === "leads" && (
          <div>
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <div className="relative flex-1">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#52525B]" />
                <input
                  data-testid="lead-search"
                  placeholder={t("admin.searchPlaceholder")}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full bg-white border border-[#E4E4E7] rounded-lg pl-9 pr-4 py-2.5 text-sm text-[#1A1A1A] outline-none focus:border-[#D4AF37]"
                />
              </div>
              <select data-testid="filter-building" value={fBuilding} onChange={(e) => setFBuilding(e.target.value)} className="bg-white border border-[#E4E4E7] rounded-lg px-4 py-2.5 text-sm text-[#1A1A1A] outline-none">
                <option value="">{t("admin.filterBuilding")}: {t("admin.all")}</option>
                <option value="Migdolas">Migdolas</option>
                <option value="Vyšnia">Vyšnia</option>
              </select>
              <select data-testid="filter-status" value={fStatus} onChange={(e) => setFStatus(e.target.value)} className="bg-white border border-[#E4E4E7] rounded-lg px-4 py-2.5 text-sm text-[#1A1A1A] outline-none">
                <option value="">{t("admin.filterStatus")}: {t("admin.all")}</option>
                {LEAD_STATUSES.map((s) => <option key={s} value={s}>{st(s)}</option>)}
              </select>
            </div>

            {/* Table */}
            <div className="bg-white border border-[#E4E4E7] rounded-lg overflow-x-auto">
              <table className="w-full text-sm" data-testid="leads-table">
                <thead>
                  <tr className="text-left text-[#52525B] border-b border-[#E4E4E7] text-xs uppercase tracking-wide">
                    <th className="px-4 py-3">{t("admin.date")}</th>
                    <th className="px-4 py-3">{t("admin.name")}</th>
                    <th className="px-4 py-3">{t("admin.contact")}</th>
                    <th className="px-4 py-3">{t("admin.buildingCol")}</th>
                    <th className="px-4 py-3">{t("admin.source")}</th>
                    <th className="px-4 py-3">{t("admin.status")}</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {leads.length === 0 && (
                    <tr><td colSpan={7} className="px-4 py-10 text-center text-[#52525B]">{t("admin.noLeads")}</td></tr>
                  )}
                  {leads.map((l) => (
                    <tr key={l.id} className="border-b border-[#F0EFEA] hover:bg-[#F5F3EE] cursor-pointer" onClick={() => { setSelected(l); }} data-testid={`lead-row-${l.id}`}>
                      <td className="px-4 py-3 text-[#52525B] whitespace-nowrap">{fmtDate(l.created_at)}</td>
                      <td className="px-4 py-3 text-[#1A1A1A] font-medium">{l.name}</td>
                      <td className="px-4 py-3 text-[#52525B]">
                        <div>{l.phone}</div>
                        <div className="text-xs">{l.email}</div>
                      </td>
                      <td className="px-4 py-3 text-[#52525B]">{l.building || "—"}{l.apartment ? ` · ${l.apartment}` : ""}</td>
                      <td className="px-4 py-3 text-[#52525B] capitalize">{l.source?.replace("_", " ")}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-block text-[10px] uppercase tracking-wide px-2 py-1 rounded-full border ${statusStyle[l.status]}`}>{st(l.status)}</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button className="text-[#8a6d1a] text-xs uppercase tracking-wide" onClick={(e) => { e.stopPropagation(); setSelected(l); }}>{t("admin.view")}</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "inventory" && (
          <div className="space-y-8" data-testid="inventory-panel">
            <h2 className="font-serif text-2xl text-[#1A1A1A]">{t("admin.inventoryTitle")}</h2>
            {Object.entries(byBuilding).map(([bname, items]) => (
              <div key={bname}>
                <h3 className="text-sm uppercase tracking-widest text-[#8a6d1a] mb-3">{bname} · {items.length} {t("common.apartments")}</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-3">
                  {items.map((i) => (
                    <div key={i.code} className="bg-white border border-[#E4E4E7] rounded-lg p-4" data-testid={`inv-${i.code}`}>
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-[#1A1A1A]">{bname === "Migdolas" ? `A${i.number}` : `B${i.number}`}</span>
                        <span className={`text-[9px] uppercase px-1.5 py-0.5 rounded border ${invStyle[i.status]}`}>{t(`common.${i.status.toLowerCase()}`)}</span>
                      </div>
                      <p className="text-xs text-[#52525B] mt-1">{t("common.floor")} {i.floor}</p>
                      <div className="flex gap-1 mt-3">
                        {INV_STATUSES.map((s) => (
                          <button
                            key={s}
                            onClick={() => updateInventory(i.code, s)}
                            className={`flex-1 text-[9px] uppercase py-1 rounded border transition-colors ${i.status === s ? invStyle[s] : "border-[#E4E4E7] text-[#52525B] hover:bg-[#F5F3EE]"}`}
                            title={t(`common.${s.toLowerCase()}`)}
                          >
                            {s[0]}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Lead detail drawer */}
      {selected && (
        <div className="fixed inset-0 z-40 flex justify-end" data-testid="lead-detail-drawer">
          <div className="absolute inset-0 bg-black/30" onClick={() => setSelected(null)} />
          <div className="relative w-full max-w-md bg-white h-full overflow-y-auto shadow-xl">
            <div className="flex items-center justify-between p-5 border-b border-[#E4E4E7] sticky top-0 bg-white">
              <h3 className="font-serif text-xl text-[#1A1A1A]">{t("admin.leadDetail")}</h3>
              <button onClick={() => setSelected(null)} data-testid="close-drawer" className="text-[#52525B] hover:text-[#1A1A1A]"><X size={20} /></button>
            </div>
            <div className="p-5 space-y-5">
              <div>
                <p className="text-lg font-medium text-[#1A1A1A]">{selected.name}</p>
                <p className="text-xs text-[#52525B]">{fmtDate(selected.created_at)} · {selected.source?.replace("_", " ")}</p>
              </div>
              <div className="space-y-2 text-sm">
                <a href={`tel:${selected.phone}`} className="flex items-center gap-2 text-[#1A1A1A]"><Phone size={15} className="text-[#8a6d1a]" /> {selected.phone}</a>
                {selected.email && <a href={`mailto:${selected.email}`} className="flex items-center gap-2 text-[#1A1A1A]"><Mail size={15} className="text-[#8a6d1a]" /> {selected.email}</a>}
              </div>
              {(selected.building || selected.apartment) && (
                <div className="bg-[#F5F3EE] rounded-lg p-3 text-sm text-[#1A1A1A]">
                  {selected.building} {selected.apartment ? `· ${selected.apartment}` : ""}
                </div>
              )}
              {selected.message && (
                <div>
                  <p className="text-[11px] uppercase tracking-widest text-[#8a6d1a] mb-1">{t("admin.message")}</p>
                  <p className="text-sm text-[#52525B] whitespace-pre-wrap">{selected.message}</p>
                </div>
              )}

              {/* status */}
              <div>
                <p className="text-[11px] uppercase tracking-widest text-[#8a6d1a] mb-2">{t("admin.updateStatus")}</p>
                <div className="flex flex-wrap gap-2">
                  {LEAD_STATUSES.map((s) => (
                    <button
                      key={s}
                      data-testid={`status-btn-${s}`}
                      onClick={() => updateStatus(selected.id, s)}
                      className={`text-[10px] uppercase tracking-wide px-2.5 py-1.5 rounded-full border transition-colors ${selected.status === s ? statusStyle[s] : "border-[#E4E4E7] text-[#52525B] hover:bg-[#F5F3EE]"}`}
                    >
                      {st(s)}
                    </button>
                  ))}
                </div>
              </div>

              {/* notes */}
              <div>
                <p className="text-[11px] uppercase tracking-widest text-[#8a6d1a] mb-2 flex items-center gap-1"><StickyNote size={13} /> {t("admin.notes")}</p>
                <div className="space-y-2 mb-3">
                  {(selected.notes || []).map((n) => (
                    <div key={n.id} className="bg-[#F5F3EE] rounded p-3 text-sm text-[#1A1A1A]">
                      <p className="whitespace-pre-wrap">{n.text}</p>
                      <p className="text-[10px] text-[#52525B] mt-1">{fmtDate(n.created_at)} · {n.author}</p>
                    </div>
                  ))}
                </div>
                <textarea
                  data-testid="note-input"
                  rows={3}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder={t("admin.notePlaceholder")}
                  className="w-full border border-[#E4E4E7] rounded-lg p-3 text-sm text-[#1A1A1A] outline-none focus:border-[#D4AF37]"
                />
                <button onClick={addNote} data-testid="add-note-btn" className="mt-2 bg-[#1A1A1A] text-white text-xs uppercase tracking-wide px-4 py-2 rounded hover:bg-[#2B2B2B]">
                  {t("admin.addNote")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
